import { CalendarBlankIcon } from "@phosphor-icons/react/dist/csr/CalendarBlank";
import { CheckSquareIcon } from "@phosphor-icons/react/dist/csr/CheckSquare";
import { FlagIcon } from "@phosphor-icons/react/dist/csr/Flag";
import { HashStraightIcon } from "@phosphor-icons/react/dist/csr/HashStraight";
import { PercentIcon } from "@phosphor-icons/react/dist/csr/Percent";
import { TextAaIcon } from "@phosphor-icons/react/dist/csr/TextAa";
import { cn } from "@volca/ui/lib/utils";
import { currencyIcon, DEFAULT_CURRENCY } from "@/profitability/currency";
import { isMissing, presentTotal } from "@/profitability/missing";
import {
  MissingValue,
  PartialNote,
  showValue,
} from "@/profitability/missing-value";
import { sums } from "@/profitability/report-object";
import {
  EMPTY_REVIEW,
  type ReviewSpec,
} from "@/profitability/review/review-spec";
import { RowSourceButton } from "@/profitability/sources/row-source-button";
import type {
  ReportColumn,
  ReportDataset,
  ReportRow,
} from "@/profitability/types";
import { VarianceLine } from "./estimate-variance";
import { rowVariance, withoutShownEstimates } from "./estimates";
import { FlagBadge, JudgedValue, RateMark } from "./judged-cells";
import {
  isQuiet,
  judgeRate,
  rateColumn,
  rateOfRows,
  rateRule,
  type RateRule,
  sharedTarget,
} from "./judgement";
import { ObjectIcon } from "./object-icon";
import type { TableColumn } from "./object-table";
import type { RecordObject, RecordRef } from "./objects";
import {
  findReferenced,
  partialFigure,
  recordId,
  recordKeyColumns,
  recordTitle,
  referenceName,
  referenceRef,
  roleColumn,
  unitOf,
} from "./objects";
import { RecordChip } from "./record-chip";
import { formatCell, isNumeric, sortableValue } from "./table-values";

const TYPE_ICON = {
  text: TextAaIcon,
  number: HashStraightIcon,
  percent: PercentIcon,
  date: CalendarBlankIcon,
  boolean: CheckSquareIcon,
} as const;

/**
 * The icon a column's header leads with: what kind of value it holds. Money
 * shows its own currency's sign, not always a dollar.
 */
export const typeIcon = (
  column: ReportColumn,
  currency: string = DEFAULT_CURRENCY,
) => {
  const Icon = column.flag
    ? FlagIcon
    : column.type === "currency"
      ? currencyIcon(currency)
      : TYPE_ICON[column.type];
  return <Icon aria-hidden />;
};

/** Whether a column totals under the table: money and counts of activity. */
const totals = (column: ReportColumn) =>
  (column.type === "currency" || column.type === "number") && sums(column);

/**
 * A column's total over the rows shown. Only the figures that are there add
 * up; the rest are counted, not taken as zero (FIN-004), and the table says
 * how many under it (`TableColumn.missing`). A balance or a running total
 * does not add up across rows.
 */
const total =
  (column: ReportColumn, currency: string) => (rows: ReportRow[]) => {
    const { sum } = presentTotal(rows, column.key);
    return sum === null ? <MissingValue /> : formatCell(sum, column, currency);
  };

/**
 * The rows' rate together (results over totals), under the rate column. It
 * takes the target's colour only when every row is held to the same one.
 */
const rateTotal =
  (
    dataset: ReportDataset,
    column: ReportColumn,
    rule: RateRule | undefined,
    currency: string,
  ) =>
  (rows: ReportRow[]) => {
    const { rate } = rateOfRows(dataset, rows);
    const target = rule ? sharedTarget(rule, rows) : null;
    return (
      <RateMark
        judgement={
          rule && rate !== null && target !== null
            ? judgeRate(rate, target, rule.better)
            : null
        }
      >
        {rate === null ? <MissingValue /> : formatCell(rate, column, currency)}
      </RateMark>
    );
  };

/** A row the rate can't be worked out for: its total or result is missing. */
const rateMissing = (dataset: ReportDataset) => {
  const keys = [roleColumn(dataset, "total"), roleColumn(dataset, "result")]
    .filter((column): column is ReportColumn => !!column)
    .map((column) => column.key);
  return (row: ReportRow) => keys.some((key) => isMissing(row[key]));
};

/**
 * A record table's columns from its dataset: the record first, then each
 * column in the order the report declared it, a link to another record as
 * that record's chip and the flag as a badge. Colour only where the report
 * judges a figure (`judgement.ts`); dates and plain text stay quiet.
 */
export const datasetColumns = ({
  object,
  objects,
  only,
  onOpenRecord,
  currency = DEFAULT_CURRENCY,
  review = EMPTY_REVIEW,
}: {
  object: RecordObject;
  objects: RecordObject[];
  only?: string[];
  onOpenRecord: (ref: RecordRef) => void;
  currency?: string;
  review?: ReviewSpec;
}): TableColumn<ReportRow>[] => {
  const { dataset, nameColumn } = object;
  const keys = dataset.key_columns ?? [];
  const rate = rateColumn(dataset);
  const rule = rateRule(dataset, review);
  // An estimate rides in its actual's cell, so its own column goes, unless
  // the block listed it by name (`only`).
  const chosen = withoutShownEstimates(
    (only ?? dataset.columns.map((column) => column.key))
      .map((key) => dataset.columns.find((column) => column.key === key))
      // The record's own column already shows its keys and its name; a
      // name that is not a key (an employee named by their payroll id) would
      // otherwise show twice.
      .filter(
        (column): column is ReportColumn =>
          !!column &&
          !keys.includes(column.key) &&
          column.key !== nameColumn.key,
      ),
    only,
  );
  const record: TableColumn<ReportRow> = {
    id: nameColumn.key,
    header: object.name,
    icon: <ObjectIcon icon={object.icon} />,
    cell: (row) => (
      <span className="inline-flex max-w-full items-center gap-1">
        <RecordChip
          object={object}
          name={String(row[nameColumn.key] ?? "")}
          trade={unitOf(object, objects, row)}
          variant="plain"
        />
        <RowSourceButton
          object={object}
          row={row}
          label={recordTitle(object, row)}
        />
      </span>
    ),
    sortValue: (row) => sortableValue(row[nameColumn.key]),
    // An id ("48217") lines its digits up.
    className: cn("min-w-40", {
      "tabular-nums": recordKeyColumns(dataset).includes(nameColumn.key),
    }),
  };
  return [
    record,
    ...chosen.map((column): TableColumn<ReportRow> => {
      const target = column.references
        ? objects.find((item) => item.dataset.id === column.references)
        : undefined;
      const header = column.label ?? column.key.replaceAll("_", " ");
      if (target)
        return {
          id: column.key,
          header,
          icon: <ObjectIcon icon={target.icon} />,
          cell: (row) =>
            row[column.key] ? (
              <RecordChip
                object={target}
                name={referenceName(target, row[column.key])}
                row={findReferenced(target, row[column.key])}
                onOpen={() =>
                  onOpenRecord(referenceRef(target, row[column.key]))
                }
              />
            ) : null,
          sortValue: (row) => sortableValue(row[column.key]),
        };
      if (column.flag)
        return {
          id: column.key,
          header,
          icon: typeIcon(column, currency),
          cell: (row) => <FlagBadge reason={row[column.key]} />,
          sortValue: (row) => sortableValue(row[column.key]),
          className: "min-w-40",
        };
      return {
        id: column.key,
        header,
        icon: typeIcon(column, currency),
        align: isNumeric(column) ? "end" : "start",
        cell: (row) => {
          const estimated = rowVariance(dataset.columns, column, row);
          return (
            <>
              <JudgedValue row={row} column={column} rule={rule}>
                {showValue(
                  formatCell(row[column.key], column, currency),
                  column,
                )}
              </JudgedValue>
              <PartialNote partial={partialFigure(dataset, row, column.key)} />
              {estimated && (
                <VarianceLine
                  variance={estimated.variance}
                  column={column}
                  currency={currency}
                />
              )}
            </>
          );
        },
        sortValue: (row) => sortableValue(row[column.key]),
        total:
          column === rate
            ? rateTotal(dataset, column, rule, currency)
            : totals(column)
              ? total(column, currency)
              : undefined,
        missing:
          column === rate
            ? rateMissing(dataset)
            : totals(column)
              ? (row) => isMissing(row[column.key])
              : undefined,
        className: cn({ "text-subtle tabular-nums": isQuiet(column) }),
      };
    }),
  ];
};

export const rowKeyOf = (object: RecordObject) => (row: ReportRow) =>
  recordId(object.dataset, row);
